import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../../../components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { NumericKeypad } from '../../../../components/Keypad/NumericKeypad';
import classes from './pin-pad-modal.module.scss';

export type PinPadModalProps = {
    isOpen: boolean;
    title?: string;
    subjectName: string;
    pinLength?: number;
    isSubmitting?: boolean;
    error?: string | null;
    cancelLabel?: string;
    onClose: () => void;
    onSubmit: (pin: string) => Promise<boolean> | boolean;
};

// Summary: Reusable PIN-entry modal used wherever the POS app needs a 4-digit
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
// numeric confirmation (employee identification, manager override, future
// price/refund overrides, etc.).
// Touches:
//   - PinPadModal styles (`pin-pad-modal.module.scss`).
//   - Any caller passing `onSubmit`; result `false` clears the buffer for
//     a one-tap retry, `true` lets the caller close/unmount the modal.
// Breaking change risk: Changing `pinLength` default or the auto-submit
// behaviour breaks the "no Enter button" UX promised in design.
export const PinPadModal: React.FC<PinPadModalProps> = ({
    isOpen,
    title = 'קוד עובד',
    subjectName,
    pinLength = 4,
    isSubmitting = false,
    error = null,
    cancelLabel = 'ביטול',
    onClose,
    onSubmit,
}) => {
    const [pin, setPin] = useState('');

    // Summary: Latest-callback ref so the auto-submit effect can
    // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
    // invoke the freshest `onSubmit` / read the freshest `isSubmitting` even
    // when the parent recreates them (e.g. tenant/employee context changes
    // mid-flight). Avoids depending on `onSubmit`/`isSubmitting` in the
    // submit effect deps, which would re-trigger submission on every re-render.
    const onSubmitRef = useRef(onSubmit);
    const isSubmittingRef = useRef(isSubmitting);
    useEffect(() => {
        onSubmitRef.current = onSubmit;
        isSubmittingRef.current = isSubmitting;
    }, [onSubmit, isSubmitting]);

    // Summary: Resets PIN buffer whenever the modal opens or the subject changes so stale digits never carry across sessions.
    // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
    useEffect(() => {
        if (isOpen) {
            setPin('');
        }
    }, [isOpen, subjectName]);

    // Summary: Auto-submits PIN once full; clears on failure for one-tap retry.
    // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
    useEffect(() => {
        if (!isOpen || pin.length !== pinLength || isSubmittingRef.current) {
            return;
        }
        let cancelled = false;
        void (async () => {
            const ok = await onSubmitRef.current(pin);
            if (!ok && !cancelled) {
                setPin('');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [pin, isOpen, pinLength]);

    if (!isOpen) {
        return null;
    }

    // Summary: Adds a single digit to the PIN buffer up to pinLength.
    // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
    const pressDigit = (digit: string) => {
        if (isSubmitting) {
            return;
        }
        setPin((previous) => (previous.length >= pinLength ? previous : previous + digit));
    };

    // Summary: Removes the last entered digit from the PIN buffer.
    // Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-pinpadmodal
    const pressBackspace = () => {
        if (isSubmitting) {
            return;
        }
        setPin((previous) => previous.slice(0, -1));
    };

    return (
        <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={title} data-testid="pin-pad-modal">
            <div className={classes.modal}>
                <div className={classes.header}>
                    <h2 className={classes.title}>{title}</h2>
                </div>

                <div className={classes.body}>
                    <p className={classes.name}>{subjectName}</p>

                    <div className={classes.display}>
                        {Array.from({ length: pinLength }).map((_, index) => (
                            <span
                                key={index}
                                className={`${classes.dot} ${index < pin.length ? classes.dotFilled : ''}`}
                            />
                        ))}
                    </div>

                    {error && <p className={classes.error}>{error}</p>}

                    <NumericKeypad
                        dataTestId="pin-pad"
                        keypadClassName={classes.keypad}
                        keyClassName={classes.key}
                        disabled={isSubmitting}
                        onDigit={pressDigit}
                        onBackspace={pressBackspace}
                        backspaceDisabled={pin.length === 0}
                        backspaceAriaLabel="מחק ספרה"
                        backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
                        leftKey={{
                            label: '.',
                            className: `${classes.key} ${classes.keyMuted}`,
                            disabled: true,
                            ariaHidden: true,
                            tabIndex: -1,
                        }}
                    />

                    <Button variant="cancel" fullWidth onClick={onClose} disabled={isSubmitting} dataTestId="pin-pad-cancel">
                        {cancelLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};
